import { Bell } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fmtDate } from "@/lib/format";
import { isInAppEnabled, useNotificationPrefs } from "@/lib/notification-prefs";

export function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const { data: prefs } = useNotificationPrefs();

  const { data: allNotifications = [] } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => {
      const { data } = await supabase
        .from("notifications")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(20);
      return data ?? [];
    },
    refetchInterval: 30_000,
  });
  const notifications = allNotifications.filter((n) => isInAppEnabled(prefs, n.type));

  const unread = notifications.filter((n) => !n.read_at);

  useEffect(() => {
    if (!open || unread.length === 0) return;
    const ids = unread.map((n) => n.id);
    supabase.rpc("mark_notifications_read", { _ids: ids }).then(() => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
    });
  }, [open, unread, qc]);

  return (
    <div className="relative">
      <button
        aria-label="Notifications"
        className="relative p-2 text-muted-foreground hover:text-foreground"
        onClick={() => setOpen((v) => !v)}
      >
        <Bell className="h-5 w-5" />
        {unread.length > 0 && (
          <span className="absolute top-1 right-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold text-destructive-foreground">
            {unread.length > 9 ? "9+" : unread.length}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-80 max-h-[70vh] z-40 overflow-y-auto rounded-xl border border-white/10 bg-background/95 backdrop-blur-xl shadow-xl">
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
              <div className="text-sm font-semibold">Notifications</div>
              <Link to="/notifications" onClick={() => setOpen(false)} className="text-xs text-primary hover:underline">
                View all
              </Link>
            </div>
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                No notifications yet
              </div>
            ) : (
              <ul className="divide-y divide-white/5">
                {notifications.slice(0, 8).map((n) => {
                  const content = (
                    <div className={`px-4 py-3 hover:bg-white/5 ${!n.read_at ? "bg-primary/5" : ""}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="text-sm font-medium">{n.title}</div>
                        {!n.read_at && <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-primary" />}
                      </div>
                      {n.body && <div className="text-xs text-muted-foreground mt-1">{n.body}</div>}
                      <div className="text-[11px] text-muted-foreground mt-1">{fmtDate(n.created_at)}</div>
                    </div>
                  );
                  return (
                    <li key={n.id}>
                      {n.link ? (
                        <a href={n.link} onClick={() => setOpen(false)}>{content}</a>
                      ) : (
                        content
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}